import React, { useCallback, useEffect, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import CopyButton from './CopyButton';
import './AdminNewsletter.css';
import './AdminServer.css';

const SERVICE_LABELS = {
    'showbot-backend': 'Сервер СВОД (сайт и API)',
    'showbot-student': 'Бот для участников',
    'showbot-teacher': 'Бот для организаторов',
    nginx: 'Веб-сервер nginx',
    postgresql: 'База данных PostgreSQL',
};

const STATE_LABELS = {
    active: 'работает',
    inactive: 'остановлена',
    failed: 'упала',
    activating: 'запускается',
    deactivating: 'останавливается',
    'not-installed': 'не установлена',
    unknown: 'нет данных',
};

const GROUP_LABELS = {
    project: 'Внутри папки СВОД',
    home: 'Домашняя папка',
    system: 'Система',
};

export function formatBytes(value) {
    if (value == null) return '—';
    const abs = Math.abs(value);
    if (abs >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(abs >= 10 * 1024 ** 3 ? 0 : 1)} ГБ`;
    if (abs >= 1024 ** 2) return `${Math.round(value / 1024 ** 2)} МБ`;
    if (abs >= 1024) return `${Math.round(value / 1024)} КБ`;
    return `${value} Б`;
}

function formatDuration(seconds) {
    if (seconds == null) return '—';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d) return `${d} дн. ${h} ч.`;
    if (h) return `${h} ч. ${m} мин.`;
    return `${m} мин.`;
}

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Meter({ percent, warn = 80, danger = 90 }) {
    const level = percent >= danger ? 'danger' : percent >= warn ? 'warning' : 'ok';
    return (
        <div className={`srv-meter srv-meter--${level}`} role="meter" aria-valuenow={Math.round(percent)} aria-valuemin={0} aria-valuemax={100}>
            <div className="srv-meter__bar" style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
        </div>
    );
}

function Growth({ value }) {
    if (value == null || Math.abs(value) < 1024 ** 2) return null;
    return <span className={`srv-growth ${value > 0 ? 'srv-growth--up' : 'srv-growth--down'}`}>{value > 0 ? '+' : '−'}{formatBytes(Math.abs(value))}</span>;
}

function HowTo({ how, label = 'Как освободить' }) {
    const [open, setOpen] = useState(false);
    if (!how) return null;
    return (
        <div className="srv-how">
            <button type="button" className="nl-link nl-link--inline" onClick={() => setOpen((v) => !v)}>
                {open ? 'Скрыть команды' : label}
            </button>
            {open && (
                <div className="srv-how__body">
                    <pre>{how}</pre>
                    <CopyButton text={how} className="btn btn--secondary btn--sm" />
                    <p className="nl-hint nl-hint--small">Выполните в терминале сервера (по SSH). СВОД сам эти команды не запускает. После очистки нажмите «Пересчитать» в разделе «Диск» — размеры обновятся.</p>
                </div>
            )}
        </div>
    );
}

function buildAlerts(info, disk) {
    const alerts = [];
    if (!info) return alerts;
    if (info.disk.percent >= 85) alerts.push({ level: 'danger', text: `Диск занят на ${info.disk.percent}% — свободно ${formatBytes(info.disk.free)}. Посмотрите раздел «Куда уходит место».` });
    if (info.ram.percent >= 90) alerts.push({ level: 'danger', text: `Память занята на ${info.ram.percent}%. Сервер может зависнуть.` });
    if (!info.swap.total) {
        alerts.push({
            level: 'warning',
            text: 'Нет файла подкачки (swap). Когда память кончается (например, при сборке сайта), сервер зависает.',
            how: 'sudo fallocate -l 2G /swapfile\nsudo chmod 600 /swapfile\nsudo mkswap /swapfile\nsudo swapon /swapfile\necho \'/swapfile none swap sw 0 0\' | sudo tee -a /etc/fstab',
        });
    }
    (info.services || []).forEach((s) => {
        if (['failed', 'inactive'].includes(s.state)) {
            alerts.push({ level: 'danger', text: `${SERVICE_LABELS[s.name] || s.name}: ${STATE_LABELS[s.state]}.`, how: `sudo systemctl restart ${s.name}\njournalctl -u ${s.name} -n 50 --no-pager` });
        } else if (s.restarts > 3) {
            alerts.push({ level: 'warning', text: `${SERVICE_LABELS[s.name] || s.name} перезапускалась ${s.restarts} раз — возможно, падает.`, how: `journalctl -u ${s.name} -n 50 --no-pager` });
        }
    });
    const cpuLoad = info.load_avg ? info.load_avg[1] / info.cpu_count : 0;
    if (cpuLoad > 1.5) alerts.push({ level: 'warning', text: `Процессор перегружен: средняя нагрузка за 5 минут ${info.load_avg[1]} при ${info.cpu_count} ядрах.` });
    (disk?.recommendations || []).forEach((r) => {
        if (r.level === 'danger' && info.disk.percent >= 85) return; // уже сказали выше
        alerts.push(r);
    });
    return alerts;
}

function DiskSection({ disk, loading, onRefresh, error }) {
    if (error) return <div className="admin-notice admin-notice--error">{error}</div>;
    if (!disk) return <p className="nl-muted">{loading ? 'Считаем размеры папок — это может занять до 30 секунд…' : 'Нет данных'}</p>;

    const used = disk.disk.used || 1;
    const g = disk.growth;
    // Пустое и мелкое (меньше 1 МБ) не показываем, если размер точный
    const items = [...disk.items]
        .filter((i) => !i.missing && (i.bytes >= 1024 ** 2 || i.denied))
        .sort((a, b) => b.bytes - a.bytes);
    const other = Math.max(0, disk.disk.used - disk.counted_bytes);
    const groups = ['project', 'home', 'system'];

    return (
        <>
            <div className="srv-disk-summary">
                <div>
                    Занято <strong>{formatBytes(disk.disk.used)}</strong> из {formatBytes(disk.disk.total)}, свободно <strong>{formatBytes(disk.disk.free)}</strong>
                    {g && <> · за период с {formatDate(g.since)}: <Growth value={g.disk_used} />{Math.abs(g.disk_used) < 1024 ** 2 && ' без изменений'}</>}
                </div>
                <div className="nl-muted srv-small">
                    Подсчитано {formatDate(disk.scanned_at)} за {disk.duration_sec} с{disk.truncated ? ' (не всё успели обойти — размеры могут быть меньше реальных)' : ''}.{' '}
                    <button type="button" className="nl-link nl-link--inline" onClick={onRefresh} disabled={loading}>
                        {loading ? 'Пересчитываем…' : 'Пересчитать'}
                    </button>
                </div>
                {!g && <div className="nl-muted srv-small">Рост за неделю появится, когда накопятся ежедневные замеры (первый — сегодня).</div>}
            </div>

            {groups.map((group) => {
                const rows = items.filter((i) => i.group === group);
                if (!rows.length) return null;
                return (
                    <div key={group} className="srv-group">
                        <h4 className="srv-group__title">
                            {GROUP_LABELS[group]}
                            {group === 'project' && <span className="nl-muted"> · вся папка СВОД: {formatBytes(disk.project.bytes)} <Growth value={g?.project} /></span>}
                        </h4>
                        {rows.map((item) => (
                            <div key={item.key} className="srv-row">
                                <div className="srv-row__head">
                                    <span className="srv-row__label">{item.label}</span>
                                    <span className="srv-row__size">
                                        {item.denied && item.bytes < 1024 ** 2 ? <span className="nl-muted">нет доступа</span> : formatBytes(item.bytes)}
                                        {' '}<Growth value={g?.items?.[item.key]} />
                                    </span>
                                </div>
                                <div className="srv-row__bar"><div style={{ width: `${Math.min(100, (item.bytes / used) * 100)}%` }} /></div>
                                <div className="nl-muted srv-small">
                                    {item.hint}
                                    {item.denied && ' У сервера СВОД нет прав читать всю папку — реальный размер может быть больше. Точный размер: '}
                                    {item.denied && <code>sudo du -sh {item.path}</code>}
                                </div>
                                <HowTo how={item.how} />
                            </div>
                        ))}
                    </div>
                );
            })}

            <div className="srv-group">
                <h4 className="srv-group__title">Остальное</h4>
                <div className="srv-row">
                    <div className="srv-row__head">
                        <span className="srv-row__label">Система Ubuntu, программы и то, что не учтено выше</span>
                        <span className="srv-row__size">≈ {formatBytes(other)}</span>
                    </div>
                    <div className="srv-row__bar srv-row__bar--muted"><div style={{ width: `${Math.min(100, (other / used) * 100)}%` }} /></div>
                    <div className="nl-muted srv-small">Обычно 3–6 ГБ. Если намного больше — посмотрите «Крупные файлы» ниже или выполните на сервере команду из подсказки.</div>
                    <HowTo how={'sudo du -xh / --max-depth=2 2>/dev/null | sort -rh | head -25'} />
                </div>
            </div>

            {disk.home_children?.length > 0 && (
                <div className="srv-group">
                    <h4 className="srv-group__title">Что лежит в домашней папке <span className="nl-muted">({disk.home})</span></h4>
                    <table className="nl-table srv-table">
                        <thead><tr><th>Папка или файл</th><th>Размер</th><th>За неделю</th></tr></thead>
                        <tbody>
                            {disk.home_children.map((c) => (
                                <tr key={c.name}>
                                    <td className="mlog-to">{c.name}{c.denied ? <span className="nl-muted"> · не всё доступно</span> : null}</td>
                                    <td>{formatBytes(c.bytes)}</td>
                                    <td><Growth value={g?.home?.[c.name]} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {disk.big_files?.length > 0 && (
                <div className="srv-group">
                    <h4 className="srv-group__title">Крупные файлы (больше 50 МБ)</h4>
                    <table className="nl-table srv-table">
                        <thead><tr><th>Файл</th><th>Размер</th><th>Изменён</th></tr></thead>
                        <tbody>
                            {disk.big_files.map((f) => (
                                <tr key={f.path}>
                                    <td className="mlog-to">{f.path}</td>
                                    <td>{formatBytes(f.bytes)}</td>
                                    <td className="mlog-time">{formatDate(f.modified)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

// Вкладка «Сервер»: нагрузка, службы, база данных и куда уходит место на диске.
function AdminServer() {
    const [info, setInfo] = useState(null);
    const [disk, setDisk] = useState(null);
    const [error, setError] = useState(null);
    const [diskError, setDiskError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [diskLoading, setDiskLoading] = useState(false);

    const loadInfo = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/server-info');
            setInfo(res.data);
            setError(null);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось получить сведения о сервере'));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDisk = useCallback(async (refresh = false) => {
        setDiskLoading(true);
        try {
            const res = await api.get('/admin/server-info/disk', { params: refresh ? { refresh: true } : {}, timeout: 120000 });
            setDisk(res.data);
            setDiskError(null);
        } catch (err) {
            setDiskError(getApiErrorMessage(err, 'Не удалось посчитать место на диске'));
        } finally {
            setDiskLoading(false);
        }
    }, []);

    useEffect(() => { loadInfo(); loadDisk(); }, [loadInfo, loadDisk]);
    useEffect(() => {
        const t = setInterval(loadInfo, 15000);
        return () => clearInterval(t);
    }, [loadInfo]);

    const alerts = buildAlerts(info, disk);

    return (
        <section className="admin-main-card">
            <div className="nl-head">
                <h2 className="admin-main-card__title">Сервер</h2>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => { loadInfo(); loadDisk(true); }} disabled={loading}>
                    Обновить
                </button>
            </div>
            <div className="nl-body">
                {error && <div className="admin-notice admin-notice--error">{error}</div>}
                {!info && !error && <p className="nl-muted">Загрузка…</p>}
                {info && (
                    <>
                        <p className="nl-muted srv-small srv-version">
                            {info.version?.commit && <>Версия СВОД: <strong>{info.version.commit}</strong> от {formatDate(info.version.date)} — {info.version.subject}<br /></>}
                            {info.hostname} · {info.os} · Python {info.backend.python} · время сервера {formatDate(info.server_time)}
                        </p>

                        {alerts.length > 0 && (
                            <div className="srv-alerts">
                                {alerts.map((a, i) => (
                                    <div key={i} className={`srv-alert srv-alert--${a.level}`}>
                                        <div>{a.text}</div>
                                        <HowTo how={a.how} label={a.key ? 'Как освободить' : 'Как исправить'} />
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="srv-cards">
                            <div className="srv-card">
                                <span>Процессор</span>
                                <strong>{Math.round(info.cpu_percent)}%</strong>
                                <Meter percent={info.cpu_percent} />
                                <em>{info.cpu_count} ядр.{info.load_avg && <> · нагрузка {info.load_avg.join(' / ')}</>}</em>
                            </div>
                            <div className="srv-card">
                                <span>Память</span>
                                <strong>{formatBytes(info.ram.used)} <small>из {formatBytes(info.ram.total)}</small></strong>
                                <Meter percent={info.ram.percent} />
                                <em>
                                    Подкачка: {info.swap.total ? `${formatBytes(info.swap.used)} из ${formatBytes(info.swap.total)}` : <b className="srv-bad">нет</b>}
                                </em>
                            </div>
                            <div className="srv-card">
                                <span>Диск</span>
                                <strong>{formatBytes(info.disk.used)} <small>из {formatBytes(info.disk.total)}</small></strong>
                                <Meter percent={info.disk.percent} warn={75} danger={85} />
                                <em>Свободно {formatBytes(info.disk.free)} ({Math.round((info.disk.free / info.disk.total) * 100)}%)</em>
                            </div>
                            <div className="srv-card">
                                <span>Работает без перезагрузки</span>
                                <strong>{formatDuration(info.uptime_seconds)}</strong>
                                <em>
                                    Сервер СВОД: {formatDuration(info.backend.uptime_seconds)} · {formatBytes(info.backend.rss)} памяти
                                    · подключений к экранам: {info.websocket_connections}
                                </em>
                            </div>
                        </div>

                        <h3 className="nl-block__title srv-section">Службы</h3>
                        <div className="admin-table-scroll">
                            <table className="nl-table srv-table">
                                <thead><tr><th>Служба</th><th>Состояние</th><th>Запущена</th><th>Память</th><th>Перезапуски</th></tr></thead>
                                <tbody>
                                    {info.services.map((s) => (
                                        <tr key={s.name}>
                                            <td>{SERVICE_LABELS[s.name] || s.name}<div className="nl-muted srv-small">{s.name}</div></td>
                                            <td><span className={`srv-dot srv-dot--${s.state}`} />{STATE_LABELS[s.state] || s.state}</td>
                                            <td className="srv-small">{s.since || '—'}</td>
                                            <td>{s.memory != null ? formatBytes(s.memory) : '—'}</td>
                                            <td>{s.restarts ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                <h3 className="nl-block__title srv-section">Куда уходит место</h3>
                <DiskSection disk={disk} loading={diskLoading} error={diskError} onRefresh={() => loadDisk(true)} />

                {info?.database && !info.database.error && (
                    <>
                        <h3 className="nl-block__title srv-section">База данных</h3>
                        <p className="srv-small">
                            {info.database.version && <>{info.database.dialect === 'postgresql' ? 'PostgreSQL ' : ''}{info.database.version} · </>}
                            Размер: <strong>{formatBytes(info.database.size)}</strong>
                        </p>
                        <div className="admin-table-scroll">
                            <table className="nl-table srv-table">
                                <thead><tr><th>Таблица</th><th>Строк (≈)</th><th>Размер</th></tr></thead>
                                <tbody>
                                    {info.database.tables.map((t) => (
                                        <tr key={t.name}>
                                            <td>{t.name}</td>
                                            <td>{t.rows.toLocaleString('ru-RU')}</td>
                                            <td>{t.bytes != null ? formatBytes(t.bytes) : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
                {info?.database?.error && <div className="admin-notice admin-notice--error">База данных: {info.database.error}</div>}
            </div>
        </section>
    );
}

export default AdminServer;
